import React, { useState } from "react";
import styles from "../../assets/stylesheets/dialog.module.scss";
import { Box, Button, Modal, Typography } from "@mui/material";
import { loader } from "graphql.macro";
import { useMutation } from "@apollo/client";
import { FormattedMessage } from "react-intl";
import Error from "../Error";

const LEAVE_ORGANIZATION = loader(
  "../../schema/mutation/LeaveOrganization.graphql"
);

interface Props {
  organizationId: string;
  organizationName: string;
  // called once the user left the organization
  onLeft?: () => void;
}

// Lets a member or an admin leave an organization, the owner can't leave it.
const LeaveOrganizationButton: React.FC<Props> = ({
  organizationId,
  organizationName,
  onLeft,
}) => {
  const [show, setShow] = useState(false);
  const [leaveOrganization, { error, loading, reset }] = useMutation(
    LEAVE_ORGANIZATION,
    {
      variables: { organizationId },
      // the organizations of the user are in the current user
      refetchQueries: ["GetCurrentUser"],
      awaitRefetchQueries: true,
    }
  );

  const handleClose = () => {
    setShow(false);
    reset();
  };

  const handleLeave = () => {
    leaveOrganization()
      .then(() => {
        setShow(false);
        onLeft?.();
      })
      .catch(() => {});
  };

  return (
    <>
      <Button onClick={() => setShow(true)} variant="outlined" color="error">
        <FormattedMessage
          id="manage-organization.leave"
          defaultMessage="Leave"
        />
      </Button>

      <Modal open={show} onClose={handleClose}>
        <Box
          className={styles.dialog}
          sx={{
            bgcolor: "background.paper",
          }}
        >
          <Typography className={styles.title}>
            <FormattedMessage
              id="manage-organization.leave-title"
              defaultMessage="Leave the organization"
            />
          </Typography>
          <Typography className={styles.secondaryText}>
            <FormattedMessage
              id="manage-organization.leave-text"
              defaultMessage="Are you sure you want to leave {name}? You will need a new invitation to join it again."
              values={{ name: organizationName }}
            />
          </Typography>
          <Error error={error} />
          <Box className={styles.actions}>
            <Button
              onClick={handleClose}
              className={styles.cancelButton}
              variant="contained"
              color="secondary"
            >
              <FormattedMessage id="actions.cancel" defaultMessage="Cancel" />
            </Button>
            <Button
              onClick={handleLeave}
              className={styles.validateButton}
              disabled={loading}
              variant="contained"
            >
              <FormattedMessage
                id="manage-organization.leave"
                defaultMessage="Leave"
              />
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default LeaveOrganizationButton;
